/**
 * Search results message — renders book-scoped search results in the chat.
 *
 * Displays matching verses with highlighted keywords and clickable references.
 */

import type { SearchResult } from '@skjv/common';
import { formatReference, type BibleLocation } from '@skjv/common';
import { bibleState } from '@/state/bible-state';

interface SearchResultsMessageProps {
  query: string;
  bookName: string;
  results: SearchResult[];
  total: number;
}

function toLocation(result: SearchResult): BibleLocation {
  return {
    testamentIndex: result.testament - 1,
    bookIndex: result.bookNumber - 1,
    chapterIndex: result.chapterNumber - 1,
    verseIndex: result.verseNumber - 1,
  };
}

function toTitleCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/(^|\.\s+|:\s+)([a-z])/g, (_, sep, char) => sep + char.toUpperCase())
    .replace(/^./, (c) => c.toUpperCase());
}

function highlightWords(text: string, query: string): React.ReactNode[] {
  const words = query.trim().toUpperCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [text];

  const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(pattern);

  return parts.map((part, i) => {
    const isMatch = words.some((w) => w === part.toUpperCase());
    return isMatch
      ? <mark key={i} className="search-highlight">{part}</mark>
      : <span key={i}>{part}</span>;
  });
}

export function SearchResultsMessage({ query, bookName, results, total }: SearchResultsMessageProps) {
  return (
    <div className="search-results-message">
      <div className="search-results-header">
        Found <strong>{total}</strong> result{total !== 1 ? 's' : ''} for
        {' '}<em>"{query}"</em> in <strong>{bookName}</strong>
      </div>

      <div className="search-results-list">
        {results.map((result, i) => {
          const location = toLocation(result);
          const ref = formatReference(location);
          const displayText = toTitleCase(result.text);

          return (
            <button
              key={i}
              className="search-result-item"
              onClick={() => bibleState.setCurrentLocation(location)}
            >
              <span className="search-result-ref">{ref}</span>
              <span className="search-result-text">
                {highlightWords(displayText, query)}
              </span>
            </button>
          );
        })}
      </div>

      {total > results.length && (
        <div className="search-results-footer">
          Showing {results.length} of {total} results
        </div>
      )}
    </div>
  );
}
